'use client'

import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@lib/cn'

const spinnerVariants = cva('orbit-spinner shrink-0', {
  variants: {
    size: {
      xs: 'size-3.5',
      sm: 'size-[18px]',
      md: 'size-6',
      lg: 'size-8'
    }
  },
  defaultVariants: {
    size: 'sm'
  }
})

const ORBIT_DOTS = [
  'M88.47,147.23c9.07-9.07,9.07-23.78,0-32.86-9.07-9.07-23.78-9.07-32.86,0-9.07,9.07-9.07,23.78,0,32.86,9.07,9.07,23.78,9.07,32.86,0Z',
  'M111.73,91.42c12.67-2.06,21.27-13.99,19.21-26.65-2.06-12.67-13.99-21.27-26.65-19.21-12.67,2.06-21.27,13.99-19.21,26.65,2.06,12.67,13.99,21.27,26.65,19.21Z',
  'M179.94,91.72c12.83,0,23.23-10.4,23.23-23.23s-10.4-23.23-23.23-23.23-23.23,10.4-23.23,23.23,10.4,23.23,23.23,23.23Z',
  'M221.26,153.42c12.49-2.95,20.22-15.46,17.27-27.95-2.95-12.49-15.46-20.22-27.95-17.27-12.49,2.95-20.22,15.46-17.27,27.95,2.95,12.49,15.46,20.22,27.95,17.27Z'
]

const USER_BODY =
  'M179.94,169.86h-72.56v.03c-12.54.33-22.61,10.58-22.61,23.2s10.06,22.87,22.61,23.2v.03h72.57c12.84,0,23.23-10.41,23.23-23.23s-10.41-23.23-23.23-23.23h-.01Z'

const USER_HEAD =
  'M163.54,150.36c10.81-10.81,10.81-28.33,0-39.14-10.81-10.81-28.33-10.81-39.14,0-10.81,10.81-10.81,28.33,0,39.14,10.81,10.81,28.33,10.81,39.14,0Z'

interface Props {
  variant?: VariantProps<typeof spinnerVariants>
  className?: string
}

const Spinner = ({ variant, className }: Props) => (
  <svg
    viewBox="46 31 200 200"
    role="status"
    aria-label="Loading"
    className={cn(spinnerVariants({ size: variant?.size }), className)}
  >
    {ORBIT_DOTS.map((path, index) => (
      <path key={path} d={path} fill="currentColor" style={{ '--dot-index': index } as React.CSSProperties} />
    ))}
    <path d={USER_HEAD} fill="currentColor" />
    <path d={USER_BODY} fill="currentColor" />
    <style jsx>{`
      @keyframes orbit-wave {
        0%,
        100% {
          opacity: 0.2;
        }
        35% {
          opacity: 1;
        }
      }

      .orbit-spinner path[style] {
        animation: orbit-wave 1.4s cubic-bezier(0.645, 0.045, 0.355, 1) infinite;
        animation-delay: calc(var(--dot-index) * 140ms - 1.4s);
      }

      @media (prefers-reduced-motion: reduce) {
        .orbit-spinner path[style] {
          animation: none;
        }
      }
    `}</style>
  </svg>
)

export default Spinner
